/**
 * What to say the moment a survey comes back from the dead, and the way back on the air.
 *
 * **The trap this exists to close.** `PATCH /:id/restore` keeps `approvalStatus` and forces
 * `isActive: false`. So a restored survey shows "Aprovada" on its card while reaching nobody, and
 * somebody who only read the tag waits for answers that will never arrive. A toast that scrolls
 * away in four seconds is not where that belongs.
 *
 * It is deliberate on the BFF's side, and the dialog says why rather than treating it as a quirk:
 * restoring is an undo of a delete, and an undo must not, by itself, open a questionnaire in front
 * of real people at the instant somebody clicks it. Delivery needs **ativa + aprovada + dentro da
 * janela**, and putting it back on the air is its own decision.
 *
 * So the dialog offers that decision, here, with one button — and the button only exists for
 * whoever can edit the survey. Someone who cannot is told where it went instead of being offered
 * something that would answer `403`.
 *
 * @module Pages/BUs/Surveys
 */

import { css } from '@emotion/css';
import { UTModal } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { Survey } from '../../../Types/Surveys.types';

const contentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-m);
  padding-bottom: var(--space-s);
  font-size: var(--font-size-sm);
  text-wrap: pretty;
`;

const noticeStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-m);
  border: 1px solid var(--color-warning, var(--color-border-strong));
  border-left: 3px solid var(--color-warning, var(--color-border-strong));
  border-radius: var(--radius-card);
  background: var(--color-surface-hover);
`;

const headlineStyle = css`
  margin: 0;
  font-weight: var(--font-weight-semibold);
`;

const lineStyle = css`
  margin: 0;
`;

const ruleStyle = css`
  margin: 0;
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const slugStyle = css`
  font-family: var(--font-mono);
`;

export interface SurveyRestoredDialogProps {
  survey: Survey;
  /** Whether to offer putting it back on the air — pairs with `survey:update`. */
  canActivate: boolean;
  isActivating: boolean;
  onActivate: (survey: Survey) => void;
  onClose: () => void;
}

export const SurveyRestoredDialog = ({
  survey,
  canActivate,
  isActivating,
  onActivate,
  onClose,
}: SurveyRestoredDialogProps): JSX.Element => (
  <UTModal
    open
    position='center'
    maxWidth='520px'
    hasButtonClose
    modalContentId='survey-restored-dialog'
    closeButtonId='survey-restored-dialog-close'
    title='Restaurada, mas fora do ar'
    content={
      <div className={contentStyle}>
        <div className={noticeStyle} role='status'>
          <p className={headlineStyle}>
            <span className={slugStyle}>{survey.slug}</span> voltou — e voltou{' '}
            <strong>desativada</strong>.
          </p>
          <p className={lineStyle}>
            O estado de aprovação foi preservado: o cartão dela diz o mesmo de antes. Mas ela{' '}
            <strong>não está sendo entregue a ninguém</strong>, e não vai ser até ser ativada.
          </p>
          <p className={ruleStyle}>
            Para ser entregue, uma pesquisa precisa estar ativa, aprovada e dentro da janela de
            coleta — as três coisas.
          </p>
        </div>

        <p className={lineStyle}>
          É de propósito. Desfazer uma exclusão não pode, sozinho, abrir um questionário na tela de
          gente de verdade no instante em que alguém clica em "Restaurar". Pôr no ar é uma decisão à
          parte — esta aqui.
        </p>

        {canActivate ? null : (
          <p className={ruleStyle}>
            Ativar exige permissão de editar pesquisas. Quem tiver, faz pelo próprio cartão ou pelo
            construtor.
          </p>
        )}
      </div>
    }
    customButtons={
      canActivate
        ? [
            {
              text: 'Deixar fora do ar',
              variant: 'secondary',
              disabled: isActivating,
              onClick: onClose,
            },
            {
              text: 'Ativar agora',
              loading: isActivating,
              disabled: isActivating,
              onClick: () => {
                onActivate(survey);
              },
            },
          ]
        : [{ text: 'Entendi', variant: 'secondary', onClick: onClose }]
    }
    onClose={onClose}
  />
);
